'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Copy, Check, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

interface LoginFormData {
  email: string;
  password: string;
  twoFaCode?: string;
  rememberMe: boolean;
}

const DEMO_CREDENTIALS = [
  { role: 'User', email: 'marcus.chen@walletx.io', password: 'Wallet@2026' },
  { role: 'Admin', email: 'admin@walletx.io', password: 'Admin@2026!' },
];

interface Props {
  onSwitchTab: () => void;
}

export default function LoginForm({ onSwitchTab }: Props) {
  const router = useRouter();
  const { signIn } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [show2FA, setShow2FA] = useState(false);
  const [pendingCredentials, setPendingCredentials] = useState<{ email: string; password: string } | null>(null);

  const { register, handleSubmit, setValue, formState: { errors } } = useForm<LoginFormData>({ defaultValues: { rememberMe: false } });

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleUseDemoCredential = (email: string, password: string) => {
    setValue('email', email);
    setValue('password', password);
  };

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      await signIn(data.email, data.password);
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();
      const role = user?.user_metadata?.role || 'user';

      // Check if 2FA is enabled for this user
      if (role !== 'admin') {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('two_fa_enabled')
          .eq('id', user?.id)
          .single();

        if (!profile?.two_fa_enabled) {
          toast.success('Welcome to Joede Wallet!');
          router.push('/two-factor-setup');
          router.refresh();
          return;
        }
      }

      toast.success(role === 'admin' ? 'Welcome back, Admin!' : 'Welcome to Joede Wallet!');
      router.push(role === 'admin' ? '/admin-dashboard' : '/user-wallet-dashboard');
      router.refresh();
    } catch (error: any) {
      setIsLoading(false);
      toast.error(error?.message || 'Invalid credentials. Please try again.');
    }
  };

  return (
    <div className="animate-slide-up">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-foreground mb-1">Welcome back</h2>
        <p className="text-muted-foreground text-sm">Sign in to access your Joede Wallet</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
        {/* Email */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-foreground" htmlFor="login-email">Email address</label>
          <input
            id="login-email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            className={`w-full px-4 py-3 rounded-xl bg-input border text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all ${errors.email ? 'border-negative' : 'border-border'}`}
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' },
            })}
          />
          {errors.email && <p className="text-negative text-xs mt-0.5">{errors.email.message}</p>}
        </div>

        {/* Password */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-foreground" htmlFor="login-password">Password</label>
          <div className="relative">
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••••••"
              className={`w-full px-4 py-3 pr-11 rounded-xl bg-input border text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all ${errors.password ? 'border-negative' : 'border-border'}`}
              {...register('password', { required: 'Password is required' })}
            />
            <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors" aria-label="Toggle password">
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.password && <p className="text-negative text-xs mt-0.5">{errors.password.message}</p>}
        </div>

        {/* Remember me + forgot */}
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" className="w-4 h-4 rounded border-border bg-input accent-primary" {...register('rememberMe')} />
            <span className="text-sm text-muted-foreground">Remember me</span>
          </label>
          <button type="button" className="text-sm text-primary hover:text-primary/80 transition-colors font-medium">Forgot password?</button>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 active:scale-95 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ minHeight: '48px' }}
        >
          {isLoading ? <Loader2 size={18} className="animate-spin" /> : (<>Sign In <ArrowRight size={16} /></>)}
        </button>

        <p className="text-center text-sm text-muted-foreground">
          Don&apos;t have an account?{' '}
          <button type="button" onClick={onSwitchTab} className="text-primary font-medium hover:text-primary/80 transition-colors">Create one</button>
        </p>
      </form>

      {/* Demo credentials */}
      <div className="mt-8 rounded-xl border border-border bg-muted/40 p-4">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Demo Accounts</p>
        <div className="flex flex-col gap-2">
          {DEMO_CREDENTIALS.map((cred) => (
            <div key={`demo-${cred.role}`} className="flex items-center justify-between gap-3 rounded-lg bg-card px-3 py-2.5">
              <div className="flex items-center gap-2 min-w-0">
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cred.role === 'Admin' ? 'bg-accent/20 text-accent' : 'bg-primary/20 text-primary'}`}>{cred.role}</span>
                <span className="text-xs text-muted-foreground truncate">{cred.email}</span>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button type="button" onClick={() => handleCopy(cred.email, `email-${cred.role}`)} className="p-1 text-muted-foreground hover:text-foreground transition-colors" aria-label="Copy email">
                  {copiedField === `email-${cred.role}` ? <Check size={13} className="text-positive" /> : <Copy size={13} />}
                </button>
                <button type="button" onClick={() => handleUseDemoCredential(cred.email, cred.password)} className="text-xs font-medium text-primary hover:text-primary/80 transition-colors px-2 py-1 rounded-md hover:bg-primary/10">Use</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}